import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SelectiveDisclosurePanel from './components/SelectiveDisclosurePanel';
import ConsentManagementPanel from './components/ConsentManagementPanel';
import ZeroKnowledgePreferences from './components/ZeroKnowledgePreferences';
import CryptographicKeyManagement from './components/CryptographicKeyManagement';
import DataRetentionSettings from './components/DataRetentionSettings';
import BlockchainPrivacyControls from './components/BlockchainPrivacyControls';
import PrivacyStatusIndicator from './components/PrivacyStatusIndicator';

const PrivacySettings = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);

  // Privacy settings state
  const [privacySettings, setPrivacySettings] = useState({
    selectiveDisclosure: {
      contexts: {
        employment: {
          permission: 'prompt',
          allowedAttributes: ['fullName', 'jobTitle', 'experience']
        },
        education: {
          permission: 'always',
          allowedAttributes: ['fullName', 'degree', 'institution']
        },
        healthcare: {
          permission: 'never',
          allowedAttributes: []
        }
      }
    },
    zeroKnowledge: {
      privacyLevel: 'balanced',
      proofType: 'zk-snark',
      enabledModes: ['selective', 'predicate'],
      alwaysUseZK: true,
      minimizeMetadata: true,
      enableCaching: false,
      advanced: {
        timeout: '60',
        complexity: 'medium',
        verifyTrustedSetup: true,
        useHardwareAcceleration: false
      }
    },
    cryptographicKeys: {
      rotationSchedule: {
        signing: '90',
        encryption: '180'
      },
      backup: {
        frequency: 'weekly',
        encrypted: true,
        multiLocation: true,
        autoVerify: true
      }
    },
    dataRetention: {
      credentials: {
        period: 'never',
        autoDelete: false,
        notifyBeforeDeletion: true
      },
      verificationHistory: {
        period: '365',
        autoDelete: true
      },
      proofGeneration: {
        period: '90',
        autoDelete: true
      },
      global: {
        privacyFirst: true,
        secureDelete: true,
        complianceMode: false
      }
    },
    blockchain: {
      privacyLevel: 'balanced',
      preferredNetwork: 'ethereum-mainnet',
      metadataDisclosure: 'standard',
      minimizeFootprint: true,
      privacyGasStrategy: false,
      enableMixing: false,
      privacyMethods: {
        ringSignatures: false,
        stealthAddresses: false,
        zkProofs: true,
        confidentialTx: false
      },
      advanced: {
        confirmationThreshold: '6',
        gasPriceStrategy: 'standard',
        enableDecoys: false,
        useTor: false,
        privacyBridges: false
      }
    }
  });

  const [privacyStatus, setPrivacyStatus] = useState({
    selectiveDisclosure: 'active',
    zeroKnowledge: 'active',
    keyManagement: 'secure',
    dataRetention: 'optimized',
    blockchainPrivacy: 'protected',
    consentManagement: 'active'
  });

  const sectionNavigation = [
    { id: 'overview', label: 'Privacy Overview', icon: 'Shield' },
    { id: 'selective-disclosure', label: 'Selective Disclosure', icon: 'Eye' },
    { id: 'consent-management', label: 'Consent Management', icon: 'Users' },
    { id: 'zero-knowledge', label: 'Zero-Knowledge Proofs', icon: 'Lock' },
    { id: 'key-management', label: 'Key Management', icon: 'Key' },
    { id: 'data-retention', label: 'Data Retention', icon: 'Clock' },
    { id: 'blockchain-privacy', label: 'Blockchain Privacy', icon: 'Link' }
  ];

  useEffect(() => {
    // Set page title
    document.title = 'Privacy Settings - VerifyChain';
    
    // Scroll to top
    window.scrollTo(0, 0);
  }, []);

  const handleSettingsChange = (newSettings) => {
    setPrivacySettings(newSettings);
    setHasUnsavedChanges(true);
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    
    try {
      // Simulate API call to save settings
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setHasUnsavedChanges(false);
      
      // Show success notification (you could implement a toast system)
      console.log('Privacy settings saved successfully');
      
    } catch (error) {
      console.error('Failed to save privacy settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetToDefaults = () => {
    setShowResetModal(true);
  };

  const confirmReset = () => {
    // Reset to default settings
    setPrivacySettings({
      selectiveDisclosure: {
        contexts: {}
      },
      zeroKnowledge: {
        privacyLevel: 'balanced',
        proofType: 'zk-snark',
        enabledModes: ['selective'],
        alwaysUseZK: false,
        minimizeMetadata: false,
        enableCaching: true,
        advanced: {
          timeout: '60',
          complexity: 'medium',
          verifyTrustedSetup: false,
          useHardwareAcceleration: false
        }
      },
      cryptographicKeys: {
        rotationSchedule: {
          signing: 'never',
          encryption: 'never'
        },
        backup: {
          frequency: 'manual',
          encrypted: false,
          multiLocation: false,
          autoVerify: false
        }
      },
      dataRetention: {
        global: {
          privacyFirst: false,
          secureDelete: true,
          complianceMode: true
        }
      },
      blockchain: {
        privacyLevel: 'transparent',
        preferredNetwork: 'ethereum-mainnet',
        metadataDisclosure: 'full',
        minimizeFootprint: false,
        privacyGasStrategy: false,
        enableMixing: false,
        privacyMethods: {
          ringSignatures: false,
          stealthAddresses: false,
          zkProofs: false,
          confidentialTx: false
        }
      }
    });
    
    setHasUnsavedChanges(true);
    setShowResetModal(false);
  };

  const handleConsentRevoke = (consentId) => {
    console.log('Revoking consent:', consentId);
    // Handle consent revocation logic
  };

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'overview':
        return <PrivacyStatusIndicator privacyStatus={privacyStatus} />;
      
      case 'selective-disclosure':
        return (
          <SelectiveDisclosurePanel
            settings={privacySettings}
            onSettingsChange={handleSettingsChange}
          />
        );
      
      case 'consent-management':
        return (
          <ConsentManagementPanel
            consents={[]}
            onConsentRevoke={handleConsentRevoke}
            onConsentUpdate={() => {}}
          />
        );
      
      case 'zero-knowledge':
        return (
          <ZeroKnowledgePreferences
            preferences={privacySettings}
            onPreferencesChange={handleSettingsChange}
          />
        );
      
      case 'key-management':
        return (
          <CryptographicKeyManagement
            keySettings={privacySettings}
            onKeySettingsChange={handleSettingsChange}
          />
        );
      
      case 'data-retention':
        return (
          <DataRetentionSettings
            retentionSettings={privacySettings?.dataRetention}
            onRetentionSettingsChange={(newRetentionSettings) => {
              handleSettingsChange({
                ...privacySettings,
                dataRetention: newRetentionSettings
              });
            }}
          />
        );
      
      case 'blockchain-privacy':
        return (
          <BlockchainPrivacyControls
            blockchainSettings={privacySettings?.blockchain}
            onBlockchainSettingsChange={(newBlockchainSettings) => {
              handleSettingsChange({
                ...privacySettings,
                blockchain: newBlockchainSettings
              });
            }}
          />
        );
      
      default:
        return <PrivacyStatusIndicator privacyStatus={privacyStatus} />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="w-10 h-10"
              >
                <Icon name="ArrowLeft" size={20} />
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Privacy Settings</h1>
                <p className="text-muted-foreground mt-1">
                  Configure comprehensive privacy controls and consent management for your digital identity
                </p>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center space-x-3">
              <Button
                variant="default"
                onClick={handleSaveSettings}
                loading={isSaving}
                disabled={!hasUnsavedChanges}
                iconName="Save"
                iconPosition="left"
              >
                {isSaving ? 'Saving Settings...' : 'Save Privacy Settings'}
              </Button>
              
              <Button
                variant="outline"
                onClick={handleResetToDefaults}
                iconName="RotateCcw"
                iconPosition="left"
              >
                Reset to Defaults
              </Button>
              
              {hasUnsavedChanges && (
                <div className="flex items-center space-x-2 text-warning">
                  <Icon name="AlertCircle" size={16} />
                  <span className="text-sm">You have unsaved changes</span>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Section Navigation */}
            <div className="lg:col-span-1">
              <div className="bg-card border border-border rounded-lg p-4 sticky top-24">
                <h3 className="font-semibold text-foreground mb-4">Privacy Controls</h3>
                <nav className="space-y-1">
                  {sectionNavigation?.map((section) => (
                    <button
                      key={section?.id}
                      onClick={() => setActiveSection(section?.id)}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        activeSection === section?.id
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon name={section?.icon} size={16} />
                      <span>{section?.label}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              {renderSectionContent()}
            </div>
          </div>
        </div>
      </div>
      {/* Reset Confirmation Modal */}
      {showResetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <Icon name="AlertTriangle" size={24} className="text-warning" />
              <h3 className="text-lg font-semibold text-foreground">Reset Privacy Settings</h3>
            </div>
            
            <p className="text-sm text-muted-foreground mb-6">
              Are you sure you want to reset all privacy settings to their default values? 
              This will remove all your custom privacy configurations and cannot be undone.
            </p>
            
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowResetModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmReset}
                className="flex-1"
              >
                Reset Settings
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivacySettings;